import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import TextInput from '../../../components/common/inputs/textInput.jsx';
import NotificationManager from '../../../utils/notificationManager';
import ModalManager from '../../../components/common/modal/ModalManager';
import { isUnknownApiEndpointError } from '../../../utils/networkErrorUtils';

const defaultRouteForm = {
  destination: '',
  gateway: '',
  interfaceName: '',
  metric: '',
};

const DEFAULT_ROUTE_VALUES = ['default', '0.0.0.0/0', '0/0', '0.0.0.0'];

const styles = {
  card: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  description: {
    marginTop: '8px',
    color: '#607284',
    fontSize: '14px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  banner: {
    marginTop: '16px',
    padding: '12px 16px',
    borderRadius: 6,
    fontSize: '13px',
  },
  warningBanner: {
    background: '#fef3c7',
    color: '#92400e',
  },
  infoBanner: {
    background: '#e0f2fe',
    color: '#075985',
  },
  tableWrap: {
    marginTop: '20px',
  },
  badge: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '999px',
    background: '#eef2ff',
    color: '#4338ca',
  },
};

const SkeletonBar = ({ width = '100%', height = 14, style = {} }) => (
  <div
    className="skeleton-blank"
    style={{
      width,
      height,
      borderRadius: '999px',
      ...style,
    }}
  />
);

const ROUTE_SKELETON_COLUMNS = [
  { flex: 4, width: '85%' },
  { flex: 3, width: '75%' },
  { flex: 3, width: '60%' },
  { flex: 2, width: '50%' },
  { flex: 2, width: '70%', isAction: true },
];

const ROUTE_SKELETON_SMALL_WIDTHS = ['80%', '70%', '60%', '50%'];
const ROUTE_SKELETON_ROWS = 3;

const isValidIpv4 = (value = '') => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  const ipRegex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipRegex.test(trimmed);
};

const isDefaultDestination = (value = '') =>
  DEFAULT_ROUTE_VALUES.includes((value || '').trim().toLowerCase());

const isValidDestination = (value = '') => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (isDefaultDestination(trimmed)) {
    return true;
  }
  if (isValidIpv4(trimmed)) {
    return true;
  }
  const parts = trimmed.split('/');
  if (parts.length !== 2) {
    return false;
  }
  if (!isValidIpv4(parts[0])) {
    return false;
  }
  const prefix = Number(parts[1]);
  return Number.isInteger(prefix) && prefix >= 0 && prefix <= 32;
};

const isKernelRoute = (route = {}) =>
  typeof route.raw === 'string' && route.raw.includes('proto kernel');

const routeKey = (route) =>
  `${route.destination || 'dest'}__${route.gateway || 'gw'}__${route.interface || ''}`;

const buildRoutePayload = (values = defaultRouteForm) => {
  const payload = {
    destination: values.destination.trim(),
  };
  if (values.gateway.trim()) {
    payload.gateway = values.gateway.trim();
  }
  if (values.interfaceName.trim()) {
    payload.interface = values.interfaceName.trim();
  }
  if (values.metric.trim()) {
    payload.metric = Number(values.metric);
  }
  return payload;
};

const ROUTES_UNSUPPORTED_MESSAGE =
  'Update the PDU firmware to view and manage static routes from Synaccess Cloud.';

export default function DeviceConfigurationPduRoutes({
  device,
  user,
  deviceOffline,
}) {
  const serial = device?.enclosureSerialNumber;
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingKey, setDeletingKey] = useState('');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [featureMessage, setFeatureMessage] = useState('');
  const [initializing, setInitializing] = useState(true);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    }),
    []
);

const RouteCreateForm = ({ canModify, deviceOffline, onSubmit, close }) => {
  const [formValues, setFormValues] = useState(defaultRouteForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    const normalized =
      value === null || value === undefined
        ? ''
        : typeof value === 'number'
        ? String(value)
        : value;

    setFormValues((prev) => ({
      ...prev,
      [field]: normalized,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
      general: '',
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!isValidDestination(formValues.destination)) {
      errors.destination =
        'Destination must be IPv4, IPv4 CIDR (192.168.1.0/24), or "default".';
    }
    if (formValues.gateway.trim() && !isValidIpv4(formValues.gateway.trim())) {
      errors.gateway = 'Gateway must be a valid IPv4 address.';
    }
    const interfaceName = formValues.interfaceName.trim();
    if (interfaceName && (interfaceName.length > 15 || !/^[a-zA-Z0-9._-]+$/.test(interfaceName))) {
      errors.interfaceName =
        'Interface may contain letters, numbers, ".", "-", "_" (max 15 chars).';
    }
    if (formValues.metric.trim()) {
      const metricNumber = Number(formValues.metric);
      if (!Number.isInteger(metricNumber) || metricNumber < 0 || metricNumber > 65535) {
        errors.metric = 'Metric must be between 0 and 65535.';
      }
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canModify || submitting) {
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(buildRoutePayload(formValues));
      close();
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.data?.error_message || 'Failed to create the route.';
      setFormErrors({ general: message });
      NotificationManager.alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      {deviceOffline && (
        <div style={{ ...styles.banner, ...styles.warningBanner, marginBottom: '16px' }}>
          Device is offline. Configuration changes are disabled until it reconnects.
        </div>
      )}
      {!deviceOffline && !canModify && (
        <div style={{ ...styles.banner, ...styles.warningBanner, marginBottom: '16px' }}>
          You do not have permission to modify device configuration.
        </div>
      )}
      <div className="">
        <h4>Create New Route</h4>
        <p style={{ marginTop: 0, marginBottom: '12px', color: '#607284', fontSize: '14px' }}>
          Add a static route to the PDU routing table.
        </p>
        <TextInput
          label="Destination"
          placeholder="192.168.1.0/24 or default"
          value={formValues.destination}
          callback={(value) => handleChange('destination', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.destination}
        />
        <TextInput
          label="Gateway (optional for directly connected routes)"
          value={formValues.gateway}
          callback={(value) => handleChange('gateway', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.gateway}
        />
        <TextInput
          label="Interface (optional, e.g. eth0.20)"
          value={formValues.interfaceName}
          callback={(value) => handleChange('interfaceName', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.interfaceName}
        />
        <TextInput
          label="Metric (optional; 0-65535)"
          type="number"
          min={0}
          max={65535}
          value={formValues.metric}
          callback={(value) => handleChange('metric', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.metric}
        />
        {formErrors.general && (
          <div style={{ color: '#b91c1c', marginTop: '12px' }}>{formErrors.general}</div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
        <button
          type="button"
          className="button secondary"
          onClick={() => close()}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="button"
          disabled={!canModify || deviceOffline || submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};
  const canModify =
    (user?.role?.editDeviceInfo ?? true) !== false &&
    !deviceOffline &&
    !featureUnavailable;

  const fetchRoutes = useCallback(async () => {
    if (!serial) {
      setInitializing(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `/v1/devices/${serial}/network/routes`,
        { headers: authHeaders }
      );
      const payload = data?.data;
      const nextRoutes = Array.isArray(payload?.routes)
        ? payload.routes
        : Array.isArray(payload)
        ? payload
        : [];
      setRoutes(nextRoutes);
      setFeatureUnavailable(false);
      setFeatureMessage('');
    } catch (err) {
      console.error(err);
      if (isUnknownApiEndpointError(err)) {
        setFeatureUnavailable(true);
        setFeatureMessage(ROUTES_UNSUPPORTED_MESSAGE);
        setRoutes([]);
        setError('');
        return;
      }
      const message =
        err?.response?.data?.message ||
        'Unable to retrieve routes from the device.';
      setError(message);
      NotificationManager.alert(message);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  }, [serial, authHeaders]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const handleRefresh = () => {
    if (!loading) {
      fetchRoutes();
    }
  };

  const modalOptions = useMemo(
    () => ({
      preventOutsideClose: true,
      modalStyle: {
        minWidth: '520px',
        width: '620px',
        maxWidth: '90vw',
      },
      contentStyle: {
        maxHeight: '80vh',
        overflowY: 'auto',
      },
    }),
    []
  );

  const handleCreateRoute = useCallback(
    async (payload) => {
      await axios.post(
        `/v1/devices/${serial}/network/routes`,
        payload,
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      NotificationManager.success('Route created successfully.');
      fetchRoutes();
    },
    [authHeaders, fetchRoutes, serial]
  );

  const openCreateRoute = useCallback(() => {
    if (!canModify) {
      return;
    }
    ModalManager.confirm(
      '',
      'Create Route',
      '',
      (status, closeModal) => {
        if (status && typeof closeModal === 'function') {
          closeModal();
        }
      },
      ({ close }) => (
        <RouteCreateForm
          canModify={canModify}
          deviceOffline={deviceOffline}
          onSubmit={handleCreateRoute}
          close={close}
        />
      ),
      true,
      true,
      modalOptions
    );
  }, [canModify, deviceOffline, handleCreateRoute, modalOptions]);

  const handleDelete = (route) => {
    if (!serial || !canModify) {
      return;
    }
    const key = routeKey(route);
    ModalManager.confirm(
      `Delete route ${route.raw || route.destination}?`,
      'Delete Route',
      'Delete',
      async (status, close) => {
        if (status) {
          try {
            setDeletingKey(key);
            await axios.delete(
              `/v1/devices/${serial}/network/routes`,
              {
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                data: {
                  destination: route.destination,
                  gateway: route.gateway,
                  interface: route.interface,
                },
              }
            );
            NotificationManager.success('Route deleted successfully.');
            fetchRoutes();
          } catch (err) {
            console.error(err);
            const message =
              err?.response?.data?.message || 'Failed to delete the route.';
            NotificationManager.alert(message);
          } finally {
            setDeletingKey('');
            if (close) close();
          }
        } else if (close) {
          close();
        }
      }
    );
  };

  const decoratedRoutes = useMemo(() => {
    const managed = [];
    const userDefined = [];
    routes.forEach((route) => {
      if (isDefaultDestination(route.destination) || isKernelRoute(route)) {
        managed.push({ route, isManaged: true });
      } else {
        userDefined.push({ route, isManaged: false });
      }
    });
    return [...userDefined, ...managed];
  }, [routes]);

  const renderRouteRow = ({ route, isManaged }) => {
    const key = routeKey(route);
    const isDeleting = deletingKey === key;
    return (
      <div className="grid-row-wrapper" key={key}>
        <div className="grid-row large-only">
          <div style={{ flex: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span>{route.destination || '—'}</span>
              {isManaged && <span style={styles.badge}>Managed</span>}
            </div>
          </div>
          <div style={{ flex: 3 }}>{route.gateway || '—'}</div>
          <div style={{ flex: 3 }}>{route.interface || '—'}</div>
          <div style={{ flex: 2 }}>{route.metric !== undefined ? route.metric : '—'}</div>
          <div style={{ flex: 2 }}>
            {isManaged || !canModify ? (
              '—'
            ) : (
              <button
                type="button"
                className="button danger"
                style={{ padding: '6px 12px' }}
                disabled={isDeleting}
                onClick={() => handleDelete(route)}
              >
                {isDeleting ? 'Removing…' : 'Delete'}
              </button>
            )}
          </div>
        </div>
        <div className="small-only" style={{ background: '#fff', padding: '10px', border: '1px solid rgba(0,0,0,0.1)', borderTop: 'none' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {route.destination || '—'} {isManaged && <span style={{ ...styles.badge, marginLeft: 6 }}>Managed</span>}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Gateway:</strong> {route.gateway || '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Interface:</strong> {route.interface || '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 8 }}>
            <strong>Metric:</strong> {route.metric !== undefined ? route.metric : '—'}
          </div>
          {!isManaged && canModify && (
            <button
              type="button"
              className="button danger"
              style={{ width: '100%' }}
              disabled={isDeleting}
              onClick={() => handleDelete(route)}
            >
              {isDeleting ? 'Removing…' : 'Delete Route'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (featureUnavailable) {
    return (
      <div style={{ ...styles.card, ...styles.infoBanner }}>
        {featureMessage || ROUTES_UNSUPPORTED_MESSAGE}
      </div>
    );
  }

  const renderSkeletonRows = () => (
    <>
      {Array.from({ length: ROUTE_SKELETON_ROWS }).map((_, rowIdx) => (
        <div className="grid-row-wrapper" key={`route-skeleton-${rowIdx}`}>
          <div className="grid-row large-only">
            {ROUTE_SKELETON_COLUMNS.map(({ flex, width, isAction }, colIdx) => (
              <div style={{ flex }} key={`route-skeleton-col-${colIdx}`}>
                <SkeletonBar
                  width={width}
                  height={isAction ? 30 : 14}
                  style={{ marginBottom: '8px' }}
                />
              </div>
            ))}
          </div>
          <div
            className="small-only"
            style={{
              background: '#fff',
              padding: '10px',
              border: '1px solid rgba(0,0,0,0.1)',
              borderTop: 'none',
            }}
          >
            {ROUTE_SKELETON_SMALL_WIDTHS.map((width, idx) => (
              <div
                key={`route-skeleton-small-${idx}`}
                style={{ marginBottom: idx === ROUTE_SKELETON_SMALL_WIDTHS.length - 1 ? 10 : 8 }}
              >
                <SkeletonBar width={width} />
              </div>
            ))}
            <SkeletonBar width="100%" height={32} />
          </div>
        </div>
      ))}
    </>
  );

  const showSkeleton = initializing || loading;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h4 style={styles.title}>PDU Routes</h4>
          <p style={styles.description}>
            View and manage static routes on the device. Default or kernel routes are read-only.
          </p>
        </div>
        <div style={styles.controls}>
          {canModify && (
            <button
              type="button"
              className="button"
              onClick={openCreateRoute}
            >
              Create Route
            </button>
          )}
          <button
            type="button"
            className="button secondary"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {deviceOffline && (
        <div style={{ ...styles.banner, ...styles.warningBanner }}>
          Device is offline. Configuration changes are disabled until it reconnects.
        </div>
      )}

      {error && (
        <div className="alert alert-warning" style={{ marginTop: '16px' }}>
          {error}
        </div>
      )}

      <div style={styles.tableWrap}>
        <div className="grid-row header large-only">
          <div style={{ flex: 4 }}>Destination</div>
          <div style={{ flex: 3 }}>Gateway</div>
          <div style={{ flex: 3 }}>Interface</div>
          <div style={{ flex: 2 }}>Metric</div>
          <div style={{ flex: 2 }}>Actions</div>
        </div>
        {showSkeleton ? (
          renderSkeletonRows()
        ) : decoratedRoutes.length === 0 ? (
          <div className="app-item">No routes found in the main routing table.</div>
        ) : (
          decoratedRoutes.map(renderRouteRow)
        )}
      </div>

    </div>
  );
}
